import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {
  IngredientMeasurementUnit,
  MixtureMeasurementUnit,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FONT_WEIGHT_PROXIMA_REGULAR, LEGACY_FONT_PROXIMA_12} from 'styles/FontConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {DraftState} from 'ui-library/hooks/useDraftState';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {Size} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {ElementKeyboardEvent} from 'util/Types';

import {
  IngredientTableData,
  ORDERED_MIXTURE_UNITS_FOR_DISPLAY,
  PresentationalIngredientTableProps,
} from '../MixtureConstants';

export function AmountInput(props: {
  className?: string;
  currentAmount: string | null;
  onChangeAmount: (newAmount: string | null) => void;
  disabled: boolean;
  onKeyDown: (e: ElementKeyboardEvent) => void;
}): JSX.Element {
  return (
    <TextInput
      className={classNames(css(styles.textInput), props.className)}
      size={Size.SMALL}
      value={props.currentAmount || ''}
      onChange={(e) => {
        const rawValue = e.target.value.trim();
        const maybeAmount = rawValue || null;
        props.onChangeAmount(maybeAmount);
      }}
      onKeyDown={props.onKeyDown}
      disabled={props.disabled}
      dataTestAttrs={{component: 'IngredientTableSubheader', element: 'amountTextInput'}}
    />
  );
}

export function UnitsSelector<T extends IngredientMeasurementUnit | MixtureMeasurementUnit>(props: {
  validUnits: Array<T>;
  currentUnits: T;
  getUnitDisplayText: (units: T) => string;
  onSelectUnits: (newUnits: T) => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <SelectDropdown
      className="disabled--withBorder"
      menu={
        <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FIT_CONTENT} value={props.currentUnits}>
          {props.validUnits.map((units) => (
            <Option key={props.getUnitDisplayText(units)} value={units} onSelect={props.onSelectUnits}>
              {props.getUnitDisplayText(units)}
            </Option>
          ))}
        </Menu>
      }
      displayName={props.getUnitDisplayText(props.currentUnits)}
      disabled={props.disabled}
    />
  );
}

type IngredientTableSubheaderProps = PresentationalIngredientTableProps & {
  allowMeasuredIngredients: boolean;
  onKeyDown: (e: ElementKeyboardEvent) => void;
};

export function IngredientTableSubheader(props: IngredientTableSubheaderProps): JSX.Element {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const setDraftMixtureUnits = (updatedUnits: MixtureMeasurementUnit) => {
    if (!props.isLoading) {
      props.draftState.updateDraftData({mixtureUnits: updatedUnits});
    }
  };
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const setDraftMixtureAmount = (updatedAmount: string) => {
    if (!props.isLoading) {
      props.draftState.updateDraftData({mixtureAmount: updatedAmount});
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const hasInheritedIngredients = () =>
    !props.isLoading && props.draftState.data.ingredients.some((ingredient) => ingredient.isInherited);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const renderPrepUnits = (draftState: DraftState<IngredientTableData>) =>
    hasInheritedIngredients() ? (
      <Tooltip title="Units are inherited from parent mixture" delay={{show: 350}}>
        <span>{draftState.data.mixtureUnits}</span>
      </Tooltip>
    ) : (
      <UnitsSelector
        validUnits={ORDERED_MIXTURE_UNITS_FOR_DISPLAY}
        currentUnits={draftState.data.mixtureUnits}
        getUnitDisplayText={(units) => units}
        onSelectUnits={(newUnits) => {
          draftState.updateDraftData({mixtureUnits: newUnits});
        }}
        disabled={!draftState.isEditing}
      />
    );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const renderPrepSubheader = () => (
    <div className="u-spacing-top u-vertical-center-items">
      <span className={classNames('u-no-wrap u-small-spacing-right', css(styles.ingredientTableSubHeader))}>
        Target Amount:
      </span>
      <div className="u-vertical-center-items">
        {props.isLoading ? (
          <HorizontalThrobber />
        ) : (
          <>
            <AmountInput
              className={classNames(css(styles.mixtureTargetAmountInput), 'u-small-spacing-right')}
              onChangeAmount={setDraftMixtureAmount}
              currentAmount={props.draftState.data.mixtureAmount}
              disabled={!props.draftState.isEditing}
              onKeyDown={props.onKeyDown}
            />
            {renderPrepUnits(props.draftState)}
          </>
        )}
      </div>
    </div>
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const renderRecipeSubheader = () => (
    <div className={classNames('u-spacing-top', css(styles.recipeUnitsHeader))}>
      <span className={classNames('u-small-spacing-right', css(styles.ingredientTableSubHeader))}>
        Ingredients Specified:
      </span>
      {props.isLoading ? (
        <HorizontalThrobber />
      ) : (
        <UnitsSelector
          validUnits={ORDERED_MIXTURE_UNITS_FOR_DISPLAY}
          currentUnits={props.draftState.data.mixtureUnits}
          getUnitDisplayText={(units) => `per ${units}`}
          onSelectUnits={setDraftMixtureUnits}
          disabled={!props.draftState.isEditing}
        />
      )}
    </div>
  );

  return props.allowMeasuredIngredients ? renderPrepSubheader() : renderRecipeSubheader();
}

const styles = StyleSheet.create({
  mixtureTargetAmountInput: {
    width: '40%',
  },
  recipeUnitsHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  ingredientTableSubHeader: {
    ...LEGACY_FONT_PROXIMA_12,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
  textInput: {
    display: 'flex',
  },
});
